/* ============================================================
 * 60ft Ink — Order wizard orchestrator (dev pilot)
 * ------------------------------------------------------------
 * Merges ft-order-builder (shirts + print locations) and
 * ft-mockup-builder (design + review) into one continuous
 * step-by-step wizard: shirts -> locations -> design per
 * selected location -> review/add-to-cart. One shared progress
 * bar, one shared Back/Next nav.
 *
 * Rendered from a FIXED, non-reorderable position in
 * product-template.liquid (not from either of the two
 * merchant-reorderable blocks the real widgets render from) —
 * both real widget roots get reparented into this shell's own
 * slot on init via appendChild, which is what keeps this working
 * regardless of block order in the theme customizer. appendChild
 * preserves bound listeners/state; this codebase already relies
 * on that same behavior elsewhere (ft-mockup-builder.js's
 * showSidePanel() relocates the copyright-checkbox element the
 * same way).
 *
 * Only ever touches ft-mockup-builder.js through the small
 * root.__ftMockupController surface it exposes in
 * orderBuilderMode — no reach-in to its internals, no changes to
 * pricing/cart/canvas logic in either widget.
 *
 * See /Users/rolandsoutar/.claude/plans/humble-wiggling-peach.md
 * for the full architecture.
 * ============================================================ */

(function () {
  'use strict';

  function waitForController(mbRoot, cb) {
    if (!mbRoot) { cb(null); return; }
    if (mbRoot.__ftMockupController) { cb(mbRoot.__ftMockupController); return; }
    // ft-mockup-builder.js's initBuilder() is async (fetches its print-zone
    // config before assigning __ftMockupController), so it may not exist
    // yet at DOMContentLoaded time even though mbRoot itself (static
    // Liquid-rendered HTML) already does. Poll briefly rather than assume
    // either order.
    var tries = 0;
    var iv = setInterval(function () {
      if (mbRoot.__ftMockupController) {
        clearInterval(iv);
        cb(mbRoot.__ftMockupController);
      } else if (++tries > 100) { // ~5s at 50ms
        clearInterval(iv);
        cb(null);
      }
    }, 50);
  }

  function initOrderWizard(shell) {
    var obRoot = document.querySelector('[data-ft-order-builder]');
    if (!obRoot) return; // shouldn't happen given the shared tag gate, but bail cleanly

    var mbRoot = document.querySelector('[data-ft-mockup-builder]');
    var hasDesignFlow = !!mbRoot;

    var slot = shell.querySelector('[data-ftw-slot]');
    var aiEntry = document.querySelector('.ft-ai-entry');
    slot.appendChild(obRoot);
    if (aiEntry && mbRoot) {
      aiEntry.hidden = true;
      slot.appendChild(aiEntry);
    }
    if (mbRoot) slot.appendChild(mbRoot);

    var progressEl = shell.querySelector('[data-ftw-progress]');
    var stepCountEl = shell.querySelector('[data-ftw-step-count]');
    var stepTitleEl = shell.querySelector('[data-ftw-step-title]');
    var backBtn = shell.querySelector('[data-ftw-back]');
    var nextBtn = shell.querySelector('[data-ftw-next]');
    var nextHintEl = shell.querySelector('[data-ftw-nav-hint]');

    // 'order' | 'mockup'. Shirts (colour/size/qty) and print locations used
    // to be two separate steps ('shirts' then 'locations') — merged into
    // one 'order' step so the customer sees quantities, print-location
    // pills, and the live price together on the very first screen, per
    // Roland's request, rather than waiting for Next to see pricing. The
    // two [data-ob-step] wrapper divs inside ft-order-builder.liquid are
    // left in place (lowest-risk change) — they're just no longer toggled
    // between; both stay visible together whenever step === 'order'.
    var step = 'order';
    var reorderChoice = shell.querySelector('[data-ftw-reorder-choice]');
    var reorderInput = shell.querySelector('[data-ftw-reorder]');
    var originalReorder = mbRoot && mbRoot.querySelector('[data-reorder-toggle]');
    function isReorder() { return !!(originalReorder && originalReorder.checked); }
    var controller = null; // set once ft-mockup-builder.js's async init resolves

    function shirtsReady() {
      var total = 0;
      obRoot.querySelectorAll('.ft-ob__qty-input').forEach(function (input) {
        total += parseInt(input.value, 10) || 0;
      });
      return total > 0;
    }

    function locationsReady() {
      return document.querySelectorAll('.pls-option[data-location] input[type="checkbox"]:checked').length > 0;
    }

    function reorderReferenceReady() {
      return !isReorder() || ['[data-reorder-order-number]', '[data-reorder-details]'].some(function(selector) {
        var input = mbRoot.querySelector(selector); return input && input.value.trim().length > 0;
      });
    }
    function orderReady() { return shirtsReady() && locationsReady() && reorderReferenceReady(); }

    // The theme's own `button { display: inline-block }` author-origin
    // rule beats the browser's default `[hidden] { display: none }`
    // regardless of specificity — same landmine already documented (and
    // worked around the same way) in ft-mockup-builder.js's
    // updateWizardNav(). Use inline style, not the hidden attribute, for
    // these two buttons.
    function setBackVisible(visible) { backBtn.style.display = visible ? '' : 'none'; }
    function setNextVisible(visible) { nextBtn.style.display = visible ? '' : 'none'; }

    // "Next: X →" / "← Back: X" rather than a bare "Next →" — so the
    // customer always knows what pressing the button actually leads to,
    // not just that something will change.
    function nextLabel(text) { return text ? 'Next: ' + text + ' →' : 'Next →'; }
    function backLabel(text) { return text ? '← Back: ' + text : '← Back'; }

    // Button reads full brand-orange even while disabled (see
    // ft-order-wizard.css) — the reason it can't be pressed yet lives here
    // instead, right next to the button, rather than a grey fade the
    // customer has to guess the meaning of.
    function setNextHint(text) {
      if (!nextHintEl) return;
      nextHintEl.textContent = text || '';
      nextHintEl.hidden = !text;
    }

    function orderHint() {
      var missingQty = !shirtsReady();
      var missingLocation = !locationsReady();
      if (missingQty && missingLocation) return 'Add a quantity and pick a print location to continue';
      if (missingQty) return 'Add a quantity to continue';
      if (missingLocation) return 'Pick a print location to continue';
      if (!reorderReferenceReady()) return 'Enter an order reference or describe your previous design to continue';
      return '';
    }

    function renderNav() {
      // Audit fix 2026-09-06: lets theme CSS hide promo popups (OptiMonk)
      // while the customer is on the design step.
      document.body.classList.toggle('ft-design-open', step === 'mockup');
      if (step === 'order') {
        setBackVisible(false);
        if (hasDesignFlow) {
          setNextVisible(true);
          var ready = orderReady();
          nextBtn.disabled = !ready;
          // Peek at what the design flow's own first step will be (already
          // determined — the live PLS-checkbox listener keeps chosenSides
          // in sync the instant a pill is toggled, well before Next is
          // pressed) so this button can name it even before handing off.
          nextBtn.textContent = isReorder() ? 'Add reorder to cart' : 'Next: Artwork →';
          setNextHint(ready ? '' : orderHint());
        } else {
          // No design flow on this product — real add-to-cart happens via
          // ft-order-builder.js's own binding to #pls-main-add-btn
          // (visible separately, unrelated to this nav) once shirts +
          // a location are picked; nothing more for this wizard's Next to do.
          setNextVisible(false);
          setNextHint('');
        }
      } else if (step === 'mockup' && controller) {
        var navState = controller.getNavState();
        setBackVisible(true); // always allow backing out of the design flow, even at its own step 0
        backBtn.textContent = backLabel(navState.backStepLabel || 'Shirts & print locations');
        setNextVisible(navState.nextVisible);
        nextBtn.disabled = navState.nextDisabled;
        var isUploading = navState.nextLabel === 'Uploading…';
        nextBtn.textContent = isUploading ? navState.nextLabel : nextLabel(navState.nextStepLabel);
        // Uploading already explains itself via the button label above —
        // only show the hint for the "nothing attached yet" case. Also
        // requires nextVisible: on the review step the wizard's own Next
        // button is hidden (the mockup builder's real Add to cart button
        // takes over) but navState.nextDisabled still reports true, so
        // without this the hint kept telling the customer to "add your
        // design" while they were already looking at the finished review
        // with a design attached (confirmed live, 2026-09-04, audit round 3).
        setNextHint((navState.nextVisible && navState.nextDisabled && !isUploading) ? (navState.blockedReason || 'Add your design to continue') : '');
      }
    }

    // Plain-language heading for whichever step is showing right now — the
    // "super super clear" ask was to always spell out where the customer
    // is and what this screen is for, on top of the dot progress bar and
    // the Next/Back labels that already exist.
    function getStepTitle() {
      if (isReorder()) return 'Choose your garments & print locations';
      if (step === 'order') return 'Choose your garment & prints';
      if (step === 'mockup' && controller) {
        var sideLabel = controller.getStepSideLabel(controller.getCurrentIndex());
        if (sideLabel === 'Review') return 'Review & add to cart';
        // "Design" implies a design tool — this is upload + placement
        // only, never anything drawn for the customer, so the heading
        // says "Upload" instead.
        return sideLabel ? ('Upload your ' + sideLabel + ' print') : 'Upload your print';
      }
      return '';
    }

    function renderProgress() {
      var total = isReorder() ? 1 : (hasDesignFlow ? 3 : 1);
      var side = controller ? controller.getStepSideLabel(controller.getCurrentIndex()) : '';
      var currentIndex = step === 'order' ? 0 : (side === 'Review' ? 2 : 1);
      shell.setAttribute('data-ft-review-active', String(step === 'mockup' && side === 'Review'));
      if (aiEntry) aiEntry.hidden = true; // Each upload area already has a working AI action.
      stepCountEl.textContent = 'Step ' + (currentIndex + 1) + ' of ' + total;
      stepTitleEl.textContent = getStepTitle();
      progressEl.innerHTML = '';
      for (var i = 0; i < total; i++) {
        var dot = document.createElement('li');
        dot.className = 'ft-ftw__progress-dot';
        var label = ['Garment', 'Artwork', 'Review'][i];
        dot.textContent = (i + 1) + '. ' + (label || 'Artwork');
        if (i === currentIndex) {
          dot.classList.add('is-current');
          dot.setAttribute('aria-current', 'step');
        }
        else if (i < currentIndex) dot.classList.add('is-done');
        progressEl.appendChild(dot);
      }
    }


    // A mobile action bar mirrors the real control; it never owns order state.
    var mobileBar = document.createElement('div');
    mobileBar.className = 'ft-ftw__mobile-bar'; mobileBar.hidden = true;
    var mobileInfo = document.createElement('span');
    var mobileNext = document.createElement('button'); mobileNext.type = 'button';
    mobileBar.appendChild(mobileInfo); mobileBar.appendChild(mobileNext); shell.appendChild(mobileBar);
    var mobileTarget = null;
    function syncMobile() {
      var rect = shell.getBoundingClientRect();
      var focused = document.activeElement;
      var editing = focused && /INPUT|TEXTAREA|SELECT/.test(focused.tagName);
      mobileTarget = nextBtn.style.display !== 'none' ? nextBtn : (mbRoot && mbRoot.querySelector('[data-review-cta]'));
      mobileBar.hidden = window.innerWidth > 767 || rect.top > 150 || rect.bottom < 180 || editing || !mobileTarget;
      document.body.classList.toggle('ft-mobile-order-active', !mobileBar.hidden);
      if (!mobileTarget) return;
      var qty = obRoot.querySelector('[data-ob-total-qty]'), price = obRoot.querySelector('[data-ob-total-price]');
      var sum = obRoot.querySelector('[data-ob-summary]');
      mobileInfo.textContent = sum && !sum.hidden ? qty.textContent + ' garments · ' + price.textContent + ' before discounts' : 'Choose quantities and print locations';
      mobileNext.textContent = mobileTarget.textContent; mobileNext.disabled = mobileTarget.disabled;
      mobileNext.title = nextHintEl ? nextHintEl.textContent : '';
    }
    mobileNext.addEventListener('click',function(){if(mobileTarget && !mobileTarget.disabled) mobileTarget.click();});
    var mobilePending = false;
    function scheduleMobile(){ if(mobilePending)return; mobilePending=true; requestAnimationFrame(function(){mobilePending=false;syncMobile();}); }
    window.addEventListener('scroll',scheduleMobile,{passive:true}); window.addEventListener('resize',scheduleMobile);
    shell.addEventListener('input',scheduleMobile); shell.addEventListener('focusin',scheduleMobile); shell.addEventListener('focusout',scheduleMobile);
    new MutationObserver(scheduleMobile).observe(nextBtn,{attributes:true,childList:true,subtree:true,characterData:true});
    if(mbRoot) { var reviewAction=mbRoot.querySelector('[data-review-cta]'); if(reviewAction)new MutationObserver(scheduleMobile).observe(reviewAction,{attributes:true,childList:true,subtree:true}); }
    syncMobile();

    // Bring the product's own measurements beside quantity selection.
    var productArea = shell.closest('[data-section-type="product"]') || document;
    var sizeTable = Array.from(productArea.querySelectorAll('.product-block .rte table, .product-single__description table')).find(function(table){return /body width|body length|chest|waist/i.test(table.textContent);});
    if(sizeTable) {
      var guide=document.createElement('details'); guide.className='ft-ob__size-guide';
      var guideTitle=document.createElement('summary'); guideTitle.textContent='Check the size guide (cm)'; guide.appendChild(guideTitle);
      var guideBody=document.createElement('div'); guideBody.appendChild(sizeTable.cloneNode(true)); guide.appendChild(guideBody);
      var qtyTable=obRoot.querySelector('.ft-ob__table'); if(qtyTable)qtyTable.before(guide);
    }

    var obSubSteps = obRoot.querySelectorAll('[data-ob-step]');

    // Cosmetic-only cross-fade between steps — CSS handles the actual
    // opacity/transform transition (scoped to .ft-ftw.is-transitioning in
    // ft-order-wizard.css), this just brackets the real DOM swap (applyStep,
    // still synchronous) with a brief pause so the fade-out finishes before
    // new content pops in and the fade-in has something settled to animate
    // from. Skipped entirely on first load (see the plain setStep('order')
    // call at the bottom of this function) — nothing to fade from yet.
    function setStep(newStep, skipTransition) {
      if (skipTransition) {
        step = newStep;
        applyStep();
        renderNav();
        renderProgress();
        return;
      }
      shell.classList.add('is-transitioning');
      setTimeout(function () {
        step = newStep;
        applyStep();
        renderNav();
        renderProgress();
        // Plain removal, not requestAnimationFrame — rAF callbacks don't
        // reliably fire on a backgrounded/inactive tab (confirmed via
        // testing: the class stayed stuck forever in that case), which
        // would leave the wizard's content permanently faded to opacity 0
        // after the very first step change. The preceding 160ms already
        // gave the browser a full paint cycle to render the faded-out
        // state, so the transition back still animates correctly without
        // needing an extra frame here.
        shell.classList.remove('is-transitioning');
      }, 160);
    }

    function applyStep() {
      if (step === 'order') {
        obRoot.hidden = false;
        obSubSteps.forEach(function (el) { el.hidden = false; });
        if (mbRoot) {
          // Keep the existing fields and listeners together, but expand them
          // directly beneath the customer's reorder choice.
          if (isReorder()) reorderChoice.insertAdjacentElement('afterend', mbRoot);
          else slot.appendChild(mbRoot);
          mbRoot.hidden = !isReorder();
        }
      } else if (step === 'mockup') {
        // obRoot itself stays visible (not hidden) here — [data-ob-summary]
        // lives directly inside it, as a sibling of the two [data-ob-step]
        // panels rather than nested inside either, specifically so the
        // real running total keeps showing through the design/upload
        // steps instead of disappearing the moment the shirts+locations
        // panels get hidden. Without this, the only price visible during
        // upload was the theme's native per-unit "Regular price" block,
        // which never reflects quantity or print-location fees.
        obSubSteps.forEach(function (el) { el.hidden = true; });
        if (mbRoot) {
          mbRoot.hidden = false;
          if (controller) controller.reenter(); // force a real re-measure now the container is visible
        }
      }
    }

    // Real bug, found 2026-10-02 from a customer's screenshot (mockup
    // builder rendering tiny in a corner of its own canvas): navigating
    // away from this page and then hitting Back restores it from the
    // browser's bfcache — a real, common mobile pattern (back button /
    // swipe-back), not a fresh reload. `step` itself survives that restore
    // fine (bfcache preserves the JS heap), but confirmed live that mbRoot
    // can come back with `hidden` still wrong relative to it — applyStep()
    // was never asked to run again, so neither the hidden-state sync nor
    // controller.reenter()'s re-measure (the exact fix for this same
    // symptom on a normal step change, see above) ever fires. Re-running
    // applyStep() on restore reuses that existing, already-correct logic
    // instead of duplicating it — 'pageshow' with persisted=true is the
    // standard signal for a bfcache restore and never fires on a normal
    // first load, so this adds no cost to the common case.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) applyStep();
    });

    // Every Back/Next press changes what's showing below the fold on a
    // long product page — without this, a customer who's scrolled down to
    // click Next has no reason to notice the screen changed at all, and
    // easily misses the mockup that just rendered for the new step. Fires
    // on click (not after the content swap) so the scroll and the fade
    // transition happen together, landing right as the new step appears.
    function scrollToWizardTop() {
      shell.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    backBtn.addEventListener('click', function () {
      if (step === 'mockup' && controller) {
        var moved = controller.goBack();
        if (!moved) setStep('order'); // was already at the design flow's own step 0 — hand back
        else { renderNav(); renderProgress(); }
        scrollToWizardTop();
      }
    });

    nextBtn.addEventListener('click', function () {
      if (step === 'order') {
        if (!orderReady()) return;
        if (isReorder()) {
          mbRoot.querySelector('[data-reorder-cta]').click();
          return;
        }
        if (hasDesignFlow && controller) setStep('mockup');
        scrollToWizardTop();
      } else if (step === 'mockup' && controller) {
        controller.goNext();
        renderNav();
        renderProgress();
        scrollToWizardTop();
      }
    });

    if (mbRoot) mbRoot.addEventListener('ft:edit-garments', function() {
      setStep('order', true);
      scrollToWizardTop();
      var firstQty = obRoot.querySelector('.ft-ob__qty-input');
      if (firstQty) firstQty.focus({preventScroll:true});
    });

    if (mbRoot) mbRoot.addEventListener('input', function(e) {
      if (e.target.matches('[data-reorder-order-number], [data-reorder-details]')) renderNav();
    });

    // Live-update Next's disabled state as the customer types a qty or
    // toggles a location, without waiting for a step transition — both now
    // live on the same combined step, so either input re-renders nav.
    obRoot.addEventListener('input', function (e) {
      if (e.target.classList.contains('ft-ob__qty-input') && step === 'order') renderNav();
    });
    document.querySelectorAll('.pls-option[data-location] input[type="checkbox"]').forEach(function (checkbox) {
      checkbox.addEventListener('change', function () {
        if (step === 'order') renderNav();
      });
    });

    // The ordering panel already shows locations and totals. Keep the legacy
    // form controls for cart submission, without displaying a duplicate card.
    var legacyLocations = document.getElementById('print-location-selector');
    if (legacyLocations) legacyLocations.style.display = 'none';
    shell.hidden = false;
    setStep('order', true);

    waitForController(mbRoot, function (resolvedController) {
      controller = resolvedController;
      hasDesignFlow = !!controller; // downgrade cleanly if the config fetch ever fails
      if (controller && originalReorder && reorderInput) {
        reorderChoice.hidden = false;
        reorderInput.disabled = false;
        reorderInput.setAttribute('aria-controls', mbRoot.id);
        reorderInput.setAttribute('aria-expanded', 'false');
        originalReorder.closest('label').style.display = 'none';
        var locationList = mbRoot.querySelector('[data-reorder-locations]');
        if (locationList) {
          locationList.style.display = 'none';
          if (locationList.previousElementSibling) locationList.previousElementSibling.style.display = 'none';
        }
        mbRoot.querySelector('[data-reorder-cta]').style.display = 'none';
        mbRoot.querySelector('.ft-mb__reorder-note').textContent = 'Tell us which previous design to use. Then select your sizes, quantities and print locations below so your reorder is priced correctly.';
        reorderInput.addEventListener('change', function () {
          originalReorder.checked = reorderInput.checked;
          originalReorder.dispatchEvent(new Event('change', { bubbles: true }));
        });
        originalReorder.addEventListener('change', function () {
          reorderInput.checked = isReorder();
          reorderInput.setAttribute('aria-expanded', String(isReorder()));
          shell.classList.toggle('is-reorder', isReorder());
          shell.querySelector('.ft-ftw__intro-sub').textContent = isReorder()
            ? 'Choose your garments and print locations, then add your reorder to cart.'
            : 'Choose your garment → add your artwork → review your order.';
          var locationHelp = obRoot.querySelector('[data-ob-step="locations"] .ft-ob__help');
          if (locationHelp) locationHelp.textContent = isReorder()
            ? 'Select every area to print with your previous design. Print costs are included in your total.'
            : 'Select every area you want printed. You’ll add artwork for each one next.';
          setStep('order', true);
        });
      }
      controller && controller.onWizardChange(function () {
        // Always re-render both, not just while inside the design flow.
        // On the combined 'order' step, Next's own label now names the
        // design flow's first step (via controller.getStepLabel(0)), so it
        // depends on wizard.chosenSides just as much as the 'mockup' step's
        // nav does — e.g. ticking a print-location pill updates
        // chosenSides via ft-mockup-builder.js's own listener, which fires
        // this callback, and the label needs to catch up right then, not
        // only once the customer has already moved into the design step.
        renderProgress();
        renderNav();
      });
      renderNav();
      renderProgress();
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    var shell = document.querySelector('[data-ft-order-wizard]');
    if (shell) initOrderWizard(shell);
  });
})();
